import { useLocation, useSearchParams } from 'react-router-dom';
import HomePage from '../pages/page/index';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Config, Data } from '@measured/puck';
import {
  checkoutService,
  LoadingPage,
  showNotification,
  StatusCode,
} from 'shared-components';
import checkoutConfig from 'puck/checkout-puck-config';
import { ConvertBase64ToJson } from 'shared-components';
import _ from 'lodash';
import { StartCheckoutPayload } from 'shared-components/src/page-builder/interface/apiServiceInterface';
import ErrorMessage from 'components/error-message';
import { IErrorResponse } from 'shared-components/src/page-builder/interface/apiInterface';
import { setCartData } from 'shared-components/src/page-builder/redux/slices/cartSlice';
import { setStoreData } from 'shared-components/src/page-builder/redux/slices/storeSlice';
import { useAppSelector } from 'hooks/reduxHooks';
import { useDispatch } from 'react-redux';
import { PageType } from 'utils/constant';

const Home = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const storeData = useAppSelector(state => state.store.storeData);
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('store')?.split('.')?.[0];
  const cartId = searchParams.get('cart');
  const pageId = searchParams.get('pageID');
  const trafficSource = searchParams.get('trafficSource');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [puckConfig, setPuckConfig] = useState<Config | null>();
  const [puckData, setPuckData] = useState<Data>();

  const handlePreloadError = (error: AxiosError) => {
    if (error?.response?.status === 400 || error?.response?.status === 404) {
      setError(
        'There was a problem with your request. Please check your information and try again.',
      );
    } else {
      setError(
        'Something went wrong! Try refreshing the page or try again later!',
      );
    }
  };

  const checkConfigData = (pageData: string, pageType: string) => {
    const configData = ConvertBase64ToJson(pageData);
    if (!_.isEmpty(configData)) {
      setPuckData(configData);
      let puckConfig;
      if (
        [
          PageType.UPSELL,
          PageType.CHECKOUT,
          PageType.CONFIRMATION,
          PageType.OTHER,
        ].includes(pageType)
      ) {
        puckConfig = checkoutConfig() as Config;
      } else {
        setError(
          'There was a problem with your request. Please check your information and try again.',
        );
      }
      setPuckConfig(puckConfig);
    }
  };

  const getPageDeatils = async () => {
    try {
      if (!shopId || !cartId) {
        showNotification(
          StatusCode.failed,
          `Invalid ${shopId ? '' : 'store Id'}  ${cartId ? '' : 'cart Id'}`,
        );
        return;
      }

      setIsLoading(true);
      const payload: StartCheckoutPayload = {
        CartID: cartId,
        ShopID: shopId,
        TrafficSource: trafficSource || '',
      };

      if (pageId) {
        const response = await checkoutService.getPageById(pageId);
        if (response?.status === 200 && response?.data) {
          checkConfigData(response?.data?.PageData, response?.data?.PageType);
        }
      } else {
        const res = await checkoutService.getCheckout(payload);
        if (res?.status === 200 && res?.data) {
          dispatch(setCartData(res.data?.Cart));
          dispatch(
            setStoreData({
              ...storeData,
              StoreID: res.data.Cart?.StoreID,
              ShopID: shopId,
            }),
          );
          checkConfigData(
            res?.data?.PageData?.PageData,
            res?.data?.PageData?.PageType,
          );
        }
      }
    } catch (error) {
      const errorResponse = error as IErrorResponse<AxiosError>;
      handlePreloadError(errorResponse.error as AxiosError);
      console.log('Error in getting page details', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPageDeatils();
  }, [pageId, pathname, searchParams]);

  return (
    <>
      {isLoading && <LoadingPage />}
      {error ? (
        <ErrorMessage errorMessage={error} />
      ) : (
        puckConfig &&
        puckData && (
          <HomePage config={puckConfig} data={puckData} pathname={pathname} />
        )
      )}
    </>
  );
};

export default Home;
